import { useCallback, useEffect, useState } from 'react';
import {
  getPlacementChecklist,
  rememberActivationPack,
  setPlacementChecked,
} from '@/services/activation/activationLocalState';
import { PlacementChecklist, PlacementSpotId } from '@/types/activation/activation-local';

/**
 * Record that this pack is now the most recently used one. Kept for the Genie
 * switcher and for deep links; Activation itself no longer routes off it —
 * /genie-hub/activation opens on the Genie list rather than reopening whatever
 * pack this browser saw last.
 */
export const useRememberActivationPack = (packId: string | undefined): void => {
  useEffect(() => {
    if (packId) rememberActivationPack(packId);
  }, [packId]);
};

export interface PlacementChecklistState {
  checklist: PlacementChecklist;
  checkedCount: number;
  toggle: (spot: PlacementSpotId) => void;
}

/** The lookbook placement checklist — client-side only, by design. */
export const usePlacementChecklist = (packId: string | undefined): PlacementChecklistState => {
  const [checklist, setChecklist] = useState<PlacementChecklist>({});

  useEffect(() => {
    setChecklist(packId ? getPlacementChecklist(packId) : {});
  }, [packId]);

  const toggle = useCallback(
    (spot: PlacementSpotId) => {
      if (!packId) return;
      setChecklist((prev) => setPlacementChecked(packId, spot, !prev[spot]));
    },
    [packId],
  );

  const checkedCount = Object.values(checklist).filter(Boolean).length;

  return { checklist, checkedCount, toggle };
};
